import { UiButtonDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  signal,
  inject,
} from '@angular/core';
import {
  WorkflowAllowedTransition,
  WorkflowDecisionRequirement,
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  WorkflowNotificationRule,
  WorkflowNotificationMessage,
  WorkflowNotificationContextProfile,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowNotificationRuleEditorComponent } from './workflow-notification-rule-editor.component';
import { WorkflowNotificationMessageEditorComponent } from './workflow-notification-message-editor.component';
import { validateNotificationMessage } from './workflow-notification-message-template';
import { WorkflowEditorPendingScope } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

type NotificationOwner =
  | WorkflowAllowedTransition
  | WorkflowDecisionRequirement;
type NotificationRules = Array<WorkflowNotificationRule | null> | null;

export interface WorkflowStepNotificationOwnerRequest {
  kind: 'transitions' | 'decisions';
  nodeId: string;
  path?: string;
  eventIdentity?: string;
}

export interface WorkflowNotificationMessageFocus {
  path: string;
  nodeId: string;
  savedDocument?: WorkflowDefinitionDocument;
}

interface NotificationEvent {
  key: string;
  identity: string;
  label: string;
  context: string;
  triggerLabel: string;
  statusPairLabel: string | null;
  supportedCompletion: boolean | null;
  kind: 'transition' | 'decision';
  transitionIndex: number | null;
  decisionIndex: number | null;
  owner: NotificationOwner | null;
  fingerprint: string;
  ownerProblem: string | null;
  problem: string | null;
}

interface NotificationEventGroup {
  identity: string;
  label: string;
  supportLabel: string | null;
  events: NotificationEvent[];
}

@Component({
  selector: 'phms-workflow-step-notifications',
  standalone: true,
  imports: [
    UiButtonDirective,
    CommonModule,
    WorkflowNotificationRuleEditorComponent,
    WorkflowNotificationMessageEditorComponent,
  ],
  templateUrl: './workflow-step-notifications.component.html',
  styleUrl: './workflow-step-notifications.component.scss',
})
export class WorkflowStepNotificationsComponent implements OnChanges {
  private static nextInstanceId = 0;
  readonly instanceId = `step-notifications-${++WorkflowStepNotificationsComponent.nextInstanceId}`;
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() nodeId: string | null = null;
  @Input() canAuthor = false;
  @Input() interactionBlocked = false;
  @Input() roleCodes: string[] = [];
  @Input() queueCodes: string[] = [];
  @Input() managerNotificationTargetCodes: string[] = [];
  @Input() externalNotificationTargetCodes: string[] = [];
  @Input() notificationContextProfiles: WorkflowNotificationContextProfile[] =
    [];
  @Input() referenceLoading = false;
  @Input() resetVersion = 0;
  @Input() messageFocus: WorkflowNotificationMessageFocus | null = null;
  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly openOwner =
    new EventEmitter<WorkflowStepNotificationOwnerRequest>();
  @ViewChild(WorkflowNotificationRuleEditorComponent)
  private notificationEditor?: WorkflowNotificationRuleEditorComponent;
  @ViewChild(WorkflowNotificationMessageEditorComponent)
  private messageEditor?: WorkflowNotificationMessageEditorComponent;
  @ViewChild('editorHeading') private editorHeading?: ElementRef<HTMLElement>;
  @ViewChild('recipientHeading')
  private recipientHeading?: ElementRef<HTMLElement>;

  readonly selectedEvent = signal<NotificationEvent | null>(null);
  readonly localError = signal<string | null>(null);
  readonly selectionWarning = signal<string | null>(null);
  readonly pending = signal(false);
  private editingSnapshot: NotificationEvent | null = null;
  private originalShape: 'absent' | 'null' | 'array' = 'absent';
  private originalMessageWasAbsent = true;
  private recipientPending = false;
  private messagePending = false;
  private rejectedChange = false;
  private rejectedScope: 'recipients' | 'message' | null = null;
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  private lastResetVersion: number | null = null;
  private selectedDocumentIdentity = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (this.lastResetVersion !== this.resetVersion || changes['nodeId']) {
      this.lastResetVersion = this.resetVersion;
      this.resetPendingChanges();
      this.loadEvent(
        this.events.find((event) => !event.problem) ?? this.events[0] ?? null,
      );
    } else if (changes['document'] && !this.hasPendingChanges()) {
      this.refreshSelection();
    }
    if (changes['messageFocus'] && this.messageFocus) {
      this.focusMessagePath(
        this.messageFocus.path,
        this.messageFocus.nodeId,
        this.messageFocus.savedDocument,
      );
    }
  }

  get selectedStep(): WorkflowDefinitionNode | null {
    return this.stepIn(this.document);
  }
  get events(): NotificationEvent[] {
    return this.eventsIn(this.document);
  }
  get usesDefaultLifecycle(): boolean {
    return !!this.selectedStep && !this.selectedStep.allowedTransitions?.length;
  }
  get eventGroups(): NotificationEventGroup[] {
    const events = this.events;
    const stepEvents = events.filter((event) => event.transitionIndex === null);
    const groups: NotificationEventGroup[] = stepEvents.length
      ? [
          {
            identity: 'step-decisions',
            label: 'Step-wide decision results',
            supportLabel: null,
            events: stepEvents,
          },
        ]
      : [];
    for (const transition of events.filter(
      (event) => event.kind === 'transition',
    )) {
      groups.push({
        identity: this.trackEvent(0, transition),
        label: transition.label,
        supportLabel: transition.ownerProblem
          ? 'Needs repair'
          : transition.supportedCompletion
            ? 'Completion action'
            : 'Retained pair · no staff action',
        events: events.filter(
          (event) => event.transitionIndex === transition.transitionIndex,
        ),
      });
    }
    return groups;
  }
  readonly trackEvent = (_: number, event: NotificationEvent): string =>
    event.ownerProblem ? event.key : event.identity;
  readonly trackEventGroup = (
    _: number,
    group: NotificationEventGroup,
  ): string => group.identity;
  get selectedOwnerKey(): string {
    return `${this.selectedDocumentIdentity}:${this.selectedEvent()?.identity ?? ''}`;
  }
  get canEditSelection(): boolean {
    return (
      this.canAuthor &&
      !this.interactionBlocked &&
      !this.referenceLoading &&
      !!this.selectedEvent()?.owner &&
      !this.selectedEvent()?.problem &&
      !this.rejectedChange
    );
  }
  get canEditMessages(): boolean {
    return (
      this.canEditSelection &&
      [
        '1.3',
        '1.4',
        '1.5',
        '1.6',
        '1.7',
        '1.8',
        '1.9',
        '1.10',
        '1.11',
        '1.12',
        '1.13',
        '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(this.document?.payloadSchemaVersion ?? '')
    );
  }
  get messageFormatNotice(): string | null {
    const version = this.document?.payloadSchemaVersion;
    if (['1.0', '1.1', '1.2'].includes(version ?? '')) {
      return 'Message authoring needs editable routing. Review and enable editable routing for this draft before configuring a message.';
    }
    if (['1.3', '1.4', '1.5'].includes(version ?? '')) {
      return "Applying a message upgrades this draft's format. Previously unused capabilities and triggers become empty lists, and previously unspecified numbering stays disabled. Existing settings are preserved.";
    }
    return null;
  }
  get stepWarning(): string | null {
    return this.selectedStep
      ? null
      : 'Choose a unique human work step to configure its notifications.';
  }

  hasPendingChanges(): boolean {
    return (
      this.pending() ||
      this.rejectedChange ||
      !!this.notificationEditor?.formDirty() ||
      !!this.messageEditor?.hasPendingChanges()
    );
  }

  selectEvent(event: NotificationEvent, interaction?: MouseEvent): void {
    if (
      this.interactionBlocked ||
      (event.key === this.selectedEvent()?.key &&
        event.identity === this.selectedEvent()?.identity)
    )
      return;
    if (!this.applyPendingChanges()) {
      this.focusPendingChanges();
      return;
    }
    const current = this.events.find(
      (candidate) => candidate.key === event.key,
    );
    if (current) {
      this.loadEvent(current);
      if (
        interaction?.detail === 0 ||
        (interaction && window.matchMedia('(max-width: 672px)').matches)
      )
        this.focusRecipientHeading();
    }
  }

  focusNotificationPath(path: string, nodeId: string): boolean {
    if (
      nodeId !== this.nodeId ||
      this.interactionBlocked ||
      !this.applyPendingChanges()
    )
      return false;
    const prefix = `$.nodes[id=${nodeId}]`;
    if (!path.startsWith(prefix)) return false;
    const suffix = path.slice(prefix.length);
    const stepDecision = /^\.requiredDecisions\[(\d+)\]$/.exec(suffix);
    const transition =
      /^\.allowedTransitions\[(\d+)\](?:\.requiredDecisions\[(\d+)\])?$/.exec(
        suffix,
      );
    if (!stepDecision && !transition) return false;
    const matches = this.events.filter((event) =>
      stepDecision
        ? event.kind === 'decision' &&
          event.transitionIndex === null &&
          event.decisionIndex === Number(stepDecision[1])
        : event.transitionIndex === Number(transition![1]) &&
          (transition![2] === undefined
            ? event.kind === 'transition'
            : event.kind === 'decision' &&
              event.decisionIndex === Number(transition![2])),
    );
    if (matches.length !== 1 || matches[0].ownerProblem) return false;
    this.loadEvent(matches[0]);
    this.focusRecipientHeading();
    return true;
  }

  focusNotificationIdentity(identity: string): boolean {
    if (this.interactionBlocked || !this.applyPendingChanges()) return false;
    const matches = this.events.filter((event) => event.identity === identity);
    if (matches.length !== 1 || matches[0].ownerProblem) {
      this.selectedEvent.set(null);
      this.selectionWarning.set(
        'The previous notification event is no longer available or unique. Choose a current event from the list to review its recipients.',
      );
      return false;
    }
    this.loadEvent(matches[0]);
    this.focusRecipientHeading();
    return true;
  }

  focusMessagePath(
    path: string,
    nodeId: string,
    savedDocument?: WorkflowDefinitionDocument,
  ): boolean {
    if (
      nodeId !== this.nodeId ||
      this.interactionBlocked ||
      !this.applyPendingChanges()
    )
      return false;
    const prefix = `$.nodes[id=${nodeId}]`;
    if (!path.startsWith(prefix) || !path.includes('.notificationMessage'))
      return false;
    const ownerPath = path
      .slice(prefix.length)
      .split('.notificationMessage')[0];
    const transition = /^\.allowedTransitions\[([^\]]+)\]/.exec(ownerPath);
    const decision = /\.requiredDecisions\[([^\]]+)\]$/.exec(ownerPath);
    const sourceStep = this.stepIn(savedDocument ?? this.document);
    let transitionKey: string | null = null;
    let decisionKey: string | null = null;
    let decisionKind: string | null = null;
    if (transition) {
      const index = /^\d+$/.test(transition[1]) ? Number(transition[1]) : null;
      if (index !== null) {
        const saved = sourceStep?.allowedTransitions?.[index];
        if (!saved) return false;
        transitionKey = transitionIdentity(saved);
      } else if (transition[1].startsWith('transition=')) {
        const pair = transition[1].slice('transition='.length).split('->');
        if (pair.length !== 2) return false;
        transitionKey = JSON.stringify(
          pair.map((value) => value.trim().toLowerCase()),
        );
      } else return false;
    }
    if (decision) {
      if (/^\d+$/.test(decision[1])) {
        const sourceTransition =
          transition && /^\d+$/.test(transition[1])
            ? sourceStep?.allowedTransitions?.[Number(transition[1])]
            : null;
        const saved = (
          transition
            ? sourceTransition?.requiredDecisions
            : sourceStep?.requiredDecisions
        )?.[Number(decision[1])];
        if (!saved) return false;
        decisionKey = decisionIdentity(saved);
      } else {
        const match = /^kind=([^,]+)$/.exec(decision[1]);
        if (!match) return false;
        decisionKind = match[1];
      }
    }
    if (!transition && !decision) return false;
    const matches = this.events.filter((event) => {
      if (transitionKey) {
        const current =
          this.selectedStep?.allowedTransitions?.[event.transitionIndex ?? -1];
        if (!current || transitionIdentity(current) !== transitionKey)
          return false;
      } else if (event.transitionIndex !== null) return false;
      if (decision)
        return (
          event.kind === 'decision' &&
          !!event.owner &&
          (decisionKey
            ? decisionIdentity(event.owner as WorkflowDecisionRequirement) ===
              decisionKey
            : (event.owner as WorkflowDecisionRequirement).decisionKind ===
              decisionKind)
        );
      return event.kind === 'transition';
    });
    if (matches.length !== 1 || matches[0].ownerProblem) {
      this.selectedEvent.set(null);
      this.selectionWarning.set(
        'The notification message owner is no longer available or unique. Choose a current event to review its message.',
      );
      return false;
    }
    this.loadEvent(matches[0]);
    const ownerKey = this.selectedOwnerKey;
    setTimeout(() => {
      if (this.ownerViews?.focusSuspended()) return;
      if (this.selectedOwnerKey === ownerKey)
        this.messageEditor?.focusPendingChanges();
    });
    return true;
  }

  private focusRecipientHeading(): void {
    setTimeout(() => {
      if (this.ownerViews?.focusSuspended()) return;
      const heading = this.recipientHeading?.nativeElement;
      heading?.focus({ preventScroll: true });
      heading?.scrollIntoView?.({ block: 'nearest' });
    });
  }

  requestOwner(kind: WorkflowStepNotificationOwnerRequest['kind']): void {
    if (
      !this.nodeId ||
      this.interactionBlocked ||
      !this.applyPendingChanges()
    ) {
      this.focusPendingChanges();
      return;
    }
    const eventIdentity = this.selectedEvent()?.identity;
    this.openOwner.emit({
      kind,
      nodeId: this.nodeId,
      ...(eventIdentity ? { eventIdentity } : {}),
    });
  }

  requestSelectedOwner(): void {
    const identity = this.selectedEvent()?.identity;
    const documentIdentity = this.selectedDocumentIdentity;
    if (
      !this.nodeId ||
      this.interactionBlocked ||
      !this.applyPendingChanges()
    ) {
      this.focusPendingChanges();
      return;
    }
    if (
      this.selectedEvent()?.identity !== identity ||
      this.selectedDocumentIdentity !== documentIdentity
    ) {
      this.localError.set(
        'This notification event changed or is no longer unique. Select its current event before opening the rule.',
      );
      return;
    }
    const current = this.resolveCurrentOwner(true);
    if (!current?.owner) return;
    const stepPath = `$.nodes[id=${this.nodeId}]`;
    const scopePath =
      current.transitionIndex === null
        ? stepPath
        : `${stepPath}.allowedTransitions[${current.transitionIndex}]`;
    const path =
      current.kind === 'decision'
        ? `${scopePath}.requiredDecisions[${current.decisionIndex}]`
        : scopePath;
    this.openOwner.emit({
      kind: current.kind === 'decision' ? 'decisions' : 'transitions',
      nodeId: this.nodeId,
      path,
      eventIdentity: current.identity,
    });
  }

  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    const owner = () => this.currentOwnerIn(latestDocument());
    const leaf = (kind: 'recipients' | 'message') => kind === 'recipients'
      ? this.notificationEditor?.pendingEditorScopes(() => owner()?.owner?.notifications ?? null)[0]
      : this.messageEditor?.pendingEditorScopes(() => owner()?.owner?.notificationMessage ?? null)[0];
    const dirty = (kind: 'recipients' | 'message') => kind === 'recipients'
      ? this.recipientPending || !!this.notificationEditor?.formDirty() || (this.rejectedChange && this.rejectedScope !== 'message')
      : this.messagePending || !!this.messageEditor?.hasPendingChanges() || (this.rejectedChange && this.rejectedScope === 'message');
    return (['recipients', 'message'] as const).map((kind): WorkflowEditorPendingScope => ({
      id: kind === 'recipients' ? 'notification-recipients' : 'notification-message',
      label: kind === 'recipients' ? 'notification recipient input' : 'notification message input',
      state: () => {
        if (!dirty(kind)) return 'clean';
        const current = owner(), latest = latestDocument();
        if (!current || !latest || this.rejectedChange) return 'stale';
        if (!this.canAuthor || this.interactionBlocked || this.referenceLoading || !leaf(kind)) return 'invalid';
        if (kind === 'message') {
          if (!['1.3', '1.4', '1.5', '1.6', '1.7', '1.8', '1.9', '1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(latest.payloadSchemaVersion)) return 'invalid';
          if (validateNotificationMessage(this.messageEditor?.draftMessage ?? null, this.notificationContextProfiles, latest.subjectTypeCode ?? '', current.kind).length) return 'invalid';
        }
        return leaf(kind)!.state();
      },
      fingerprint: () => JSON.stringify([dirty(kind), this.selectedDocumentIdentity, this.documentIdentity(latestDocument()),
        this.editingSnapshot ?? this.selectedEvent(), this.eventsIn(latestDocument()).filter(event => event.identity === (this.editingSnapshot ?? this.selectedEvent())?.identity),
        leaf(kind)?.fingerprint() ?? null, this.rejectedChange, this.rejectedScope,
        this.canAuthor, this.interactionBlocked, this.referenceLoading, this.notificationContextProfiles, latestDocument()?.subjectTypeCode]),
      apply: () => this.applyPendingScope(kind, latestDocument()),
      discard: () => {
        const latest = latestDocument();
        const identity = (this.editingSnapshot ?? this.selectedEvent())?.identity;
        const matches = this.eventsIn(latest).filter(event => event.identity === identity);
        const current = matches.length === 1 ? matches[0] : null;
        this.document = latest;
        if (kind === 'recipients') {
          if (this.notificationEditor) { this.notificationEditor.rules = current?.owner?.notifications ?? null; this.notificationEditor.discardPendingChanges(); }
          this.recipientPending = false;
          this.originalShape = current?.owner?.notifications === undefined ? 'absent' : current.owner.notifications === null ? 'null' : 'array';
        } else {
          if (this.messageEditor) { this.messageEditor.message = current?.owner?.notificationMessage ?? null; this.messageEditor.discardPendingChanges(); }
          this.messagePending = false;
          this.originalMessageWasAbsent = current?.owner?.notificationMessage === undefined;
        }
        if (this.rejectedScope === kind || this.rejectedScope === null) { this.rejectedChange = false; this.rejectedScope = null; }
        if (current) this.selectedEvent.set(current);
        this.editingSnapshot = this.recipientPending || this.messagePending ? current : null;
        this.localError.set(null);
        this.setPending(this.recipientPending || this.messagePending || this.rejectedChange);
      },
      focus: () => kind === 'recipients' ? this.notificationEditor?.focusPendingChanges() : this.messageEditor?.focusPendingChanges(),
    }));
  }
  private applyPendingScope(kind: 'recipients' | 'message', latest: WorkflowDefinitionDocument | null): boolean {
    if (!this.canAuthor || this.interactionBlocked || this.referenceLoading || this.rejectedChange || !latest) return false;
    this.document = latest;
    const current = this.resolveCurrentOwner();
    if (!current) return false;
    if (kind === 'message') {
      if (!this.messageEditor) return !this.messagePending;
      this.messageEditor.canAuthor = this.canEditMessages;
      this.messageEditor.interactionBlocked = this.interactionBlocked;
      this.messageEditor.referenceLoading = this.referenceLoading;
      this.messageEditor.contextProfiles = this.notificationContextProfiles;
      this.messageEditor.subjectTypeCode = latest.subjectTypeCode ?? '';
      return this.messageEditor.applyPendingChanges(current.owner?.notificationMessage ?? null) && !this.rejectedChange && !this.messagePending;
    }
    if (!this.notificationEditor) return !this.recipientPending;
    this.notificationEditor.canAuthor = this.canAuthor;
    this.notificationEditor.interactionBlocked = this.interactionBlocked;
    this.notificationEditor.referenceLoading = this.referenceLoading;
    return this.notificationEditor.applyPendingChanges(current.owner?.notifications ?? null) && !this.rejectedChange && !this.recipientPending;
  }
  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (!this.hasPendingChanges()) return true;
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      this.rejectedChange
    )
      return false;
    if (latestDocument) this.document = latestDocument;
    let current = this.resolveCurrentOwner();
    if (!current) return false;
    if (this.messagePending || this.messageEditor?.hasPendingChanges()) {
      if (!this.messageEditor) return false;
      this.messageEditor.canAuthor = this.canEditMessages;
      this.messageEditor.interactionBlocked = this.interactionBlocked;
      this.messageEditor.referenceLoading = this.referenceLoading;
      this.messageEditor.contextProfiles = this.notificationContextProfiles;
      this.messageEditor.subjectTypeCode = this.document?.subjectTypeCode ?? '';
      if (
        !this.messageEditor.applyPendingChanges(
          current.owner?.notificationMessage ?? null,
        ) ||
        this.rejectedChange
      )
        return false;
      current = this.resolveCurrentOwner();
      if (!current) return false;
    }
    if (!this.recipientPending && !this.notificationEditor?.formDirty())
      return !this.hasPendingChanges();
    if (!this.notificationEditor) return false;
    this.notificationEditor.canAuthor = this.canAuthor;
    this.notificationEditor.interactionBlocked = this.interactionBlocked;
    this.notificationEditor.referenceLoading = this.referenceLoading;
    return (
      this.notificationEditor.applyPendingChanges(
        current.owner?.notifications ?? null,
      ) && !this.hasPendingChanges()
    );
  }

  resetPendingChanges(): void {
    this.notificationEditor?.discardPendingChanges();
    this.messageEditor?.discardPendingChanges();
    this.recipientPending = false;
    this.messagePending = false;
    this.rejectedChange = false;
    this.rejectedScope = null;
    this.editingSnapshot = null;
    this.localError.set(null);
    this.setPending(false);
    this.refreshSelection();
  }

  focusPendingChanges(): void {
    if (this.messagePending || this.messageEditor?.hasPendingChanges())
      this.messageEditor?.focusPendingChanges();
    else if (this.notificationEditor)
      this.notificationEditor.focusPendingChanges();
    else setTimeout(() => { if (!this.ownerViews?.focusSuspended()) this.editorHeading?.nativeElement.focus(); });
  }

  onPendingChange(value: boolean): void {
    this.recipientPending = value;
    this.updatePendingState(value);
  }

  onMessagePendingChange(value: boolean): void {
    this.messagePending = value;
    this.updatePendingState(value);
  }

  private updatePendingState(value: boolean): void {
    if (value && !this.editingSnapshot)
      this.editingSnapshot = this.selectedEvent();
    this.setPending(
      this.recipientPending || this.messagePending || this.rejectedChange,
    );
    if (!value)
      queueMicrotask(() => {
        if (!this.hasPendingChanges()) {
          this.editingSnapshot = null;
          this.refreshSelection();
        }
      });
  }

  applyNotifications(
    rules: NotificationRules,
    repairingInvalidRecipient = false,
  ): void {
    this.rejectedScope = 'recipients';
    if (!this.canAuthor || this.interactionBlocked || this.referenceLoading) {
      this.rejectChange(
        'Notification edits could not be applied. Reset the form or restore editing access.',
      );
      return;
    }
    const current = this.resolveCurrentOwner(repairingInvalidRecipient);
    const document = this.document;
    const step = this.selectedStep;
    if (!current?.owner || !document || !step) {
      this.rejectedChange = true;
      this.setPending(true);
      return;
    }
    const existing = current.owner.notifications ?? [];
    const notifications =
      rules?.map((rule) => {
        if (!rule) return null;
        const matching = existing.filter(
          (candidate) => candidate?.id === rule.id,
        );
        return matching.length === 1 ? { ...matching[0], ...rule } : rule;
      }) ?? (this.originalShape === 'array' ? [] : null);
    const owner = { ...current.owner };
    if (notifications === null && this.originalShape === 'absent')
      delete owner.notifications;
    else owner.notifications = notifications;
    this.recipientPending = false;
    this.applyOwner(current, owner, notifications?.length ? '1.1' : null);
  }

  applyMessage(message: WorkflowNotificationMessage | null): void {
    this.rejectedScope = 'message';
    if (!this.canAuthor || this.interactionBlocked || this.referenceLoading) {
      this.rejectChange(
        'Message edits could not be applied. Reset the input or restore editing access.',
      );
      return;
    }
    if (message && !this.canEditMessages) {
      this.rejectChange(
        this.messageFormatNotice ??
          'This workflow format does not support message authoring.',
      );
      return;
    }
    const current = this.resolveCurrentOwner();
    if (!current?.owner || !this.document) {
      this.rejectedChange = true;
      this.setPending(true);
      return;
    }
    if (message) {
      const issues = validateNotificationMessage(
        message,
        this.notificationContextProfiles,
        this.document.subjectTypeCode ?? '',
        current.kind,
      );
      if (issues.length) {
        this.rejectChange(issues[0].message);
        return;
      }
    }
    const owner = { ...current.owner };
    if (message === null && this.originalMessageWasAbsent)
      delete owner.notificationMessage;
    else owner.notificationMessage = message;
    this.messagePending = false;
    this.applyOwner(current, owner, message ? '1.9' : null);
  }

  private applyOwner(
    current: NotificationEvent,
    owner: NotificationOwner,
    requiredVersion: string | null,
  ): void {
    const document = this.document;
    const step = this.selectedStep;
    if (!document || !step) return;
    let nextStep: WorkflowDefinitionNode;
    if (current.kind === 'transition') {
      nextStep = {
        ...step,
        allowedTransitions: step.allowedTransitions!.map((transition, index) =>
          index === current.transitionIndex
            ? (owner as WorkflowAllowedTransition)
            : transition,
        ),
      };
    } else if (current.transitionIndex === null) {
      nextStep = {
        ...step,
        requiredDecisions: step.requiredDecisions!.map((decision, index) =>
          index === current.decisionIndex
            ? (owner as WorkflowDecisionRequirement)
            : decision,
        ),
      };
    } else {
      nextStep = {
        ...step,
        allowedTransitions: step.allowedTransitions!.map((transition, index) =>
          index === current.transitionIndex && transition
            ? {
                ...transition,
                requiredDecisions: transition.requiredDecisions!.map(
                  (decision, decisionIndex) =>
                    decisionIndex === current.decisionIndex
                      ? (owner as WorkflowDecisionRequirement)
                      : decision,
                ),
              }
            : transition,
        ),
      };
    }
    const previousVersion = document.payloadSchemaVersion;
    const messageUpgradeDefaults =
      requiredVersion === '1.9'
        ? {
            ...(previousVersion === '1.3'
              ? { capabilityReferences: document.capabilityReferences ?? [] }
              : {}),
            ...(['1.3', '1.4'].includes(previousVersion)
              ? { triggers: document.triggers ?? [] }
              : {}),
            ...(['1.3', '1.4', '1.5'].includes(previousVersion)
              ? {
                  numbering: document.numbering ?? {
                    mode: 'disabled' as const,
                  },
                }
              : {}),
          }
        : {};
    const next = {
      ...document,
      ...messageUpgradeDefaults,
      payloadSchemaVersion: requiredVersion
        ? requireWorkflowPayloadVersion(
            document.payloadSchemaVersion,
            requiredVersion,
          )
        : document.payloadSchemaVersion,
      nodes: document.nodes.map((node) => (node === step ? nextStep : node)),
    };
    this.document = next;
    this.selectedEvent.set(
      this.events.find((event) => event.identity === current.identity) ?? null,
    );
    this.editingSnapshot =
      this.recipientPending || this.messagePending
        ? this.selectedEvent()
        : null;
    this.rejectedChange = false;
    this.rejectedScope = null;
    this.localError.set(null);
    this.setPending(this.recipientPending || this.messagePending);
    this.documentChange.emit(next);
  }

  recipientSummary(event: NotificationEvent): string {
    const count = event.owner?.notifications?.filter(Boolean).length ?? 0;
    const invalid = event.owner?.notifications?.some((rule) => rule === null);
    return `${count} recipient${count === 1 ? '' : 's'}${invalid ? ' · incomplete entry' : ''}`;
  }

  eventTrigger(event: NotificationEvent): string {
    return event.kind === 'transition'
      ? 'After this status change completes'
      : 'When this decision result is recorded';
  }

  eventScope(event: NotificationEvent): string {
    const step = this.selectedStep;
    const stepLabel =
      step?.displayName?.trim() ||
      step?.stepCode ||
      step?.id ||
      'Selected step';
    return event.statusPairLabel
      ? `${stepLabel} · ${event.statusPairLabel}`
      : `${stepLabel} · step-wide`;
  }

  eventOwner(event: NotificationEvent): string {
    return event.kind === 'transition'
      ? 'Status rule'
      : event.transitionIndex === null
        ? 'Step-wide decision check'
        : 'Decision check on this status rule';
  }

  get invalidRecipientIndexes(): number[] {
    const rules = this.selectedEvent()?.owner?.notifications ?? [];
    return rules.flatMap((rule, index) =>
      !rule?.id?.trim() ||
      rules.filter((candidate) => candidate?.id === rule.id).length > 1
        ? [index]
        : [],
    );
  }

  removeInvalidRecipient(index: number): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      this.hasPendingChanges()
    )
      return;
    const owner = this.resolveCurrentOwner(true);
    if (!owner?.owner || !this.invalidRecipientIndexes.includes(index)) return;
    this.applyNotifications(
      owner.owner.notifications!.filter((_, position) => position !== index),
      true,
    );
  }

  private resolveCurrentOwner(forInspection = false): NotificationEvent | null {
    const current = this.currentOwnerIn(this.document, forInspection);
    if (!current) this.localError.set('This notification event changed or is no longer unique. Your input remains open; reset it to review the current event.');
    return current;
  }
  private currentOwnerIn(document: WorkflowDefinitionDocument | null, forInspection = false): NotificationEvent | null {
    const snapshot = this.editingSnapshot ?? this.selectedEvent();
    const matches = this.eventsIn(document).filter(
      (event) => event.identity === snapshot?.identity,
    );
    if (
      !snapshot ||
      this.selectedDocumentIdentity !== this.documentIdentity(document) ||
      matches.length !== 1 ||
      (forInspection ? matches[0].ownerProblem : matches[0].problem) ||
      matches[0].fingerprint !== snapshot.fingerprint
    ) {
      return null;
    }
    return matches[0];
  }

  private refreshSelection(): void {
    if (this.selectionWarning()) return;
    const selected = this.selectedEvent();
    const matches = this.events.filter(
      (event) => event.identity === selected?.identity,
    );
    if (
      matches.length === 1 &&
      matches[0].fingerprint === selected?.fingerprint &&
      this.selectedDocumentIdentity === this.documentIdentity()
    ) {
      this.selectedEvent.set(matches[0]);
      return;
    }
    this.loadEvent(
      matches.length === 1
        ? matches[0]
        : (this.events.find((event) => !event.problem) ??
            this.events[0] ??
            null),
    );
  }

  private loadEvent(event: NotificationEvent | null): void {
    this.selectedEvent.set(event);
    this.selectionWarning.set(null);
    this.selectedDocumentIdentity = this.documentIdentity();
    this.originalShape =
      event?.owner?.notifications === undefined
        ? 'absent'
        : event.owner.notifications === null
          ? 'null'
          : 'array';
    this.originalMessageWasAbsent =
      event?.owner?.notificationMessage === undefined;
    this.editingSnapshot = null;
    this.localError.set(null);
  }

  private stepIn(
    document: WorkflowDefinitionDocument | null,
  ): WorkflowDefinitionNode | null {
    const nodes = (document?.nodes ?? []).filter(
      (node) => node?.id === this.nodeId,
    );
    return nodes.length === 1 && nodes[0].typeCode === 'human_step'
      ? nodes[0]
      : null;
  }

  private eventsIn(
    document: WorkflowDefinitionDocument | null,
  ): NotificationEvent[] {
    const step = this.stepIn(document);
    if (!step) return [];
    const events: NotificationEvent[] = [];
    const addDecisions = (
      decisions: Array<WorkflowDecisionRequirement | null> | null,
      transitionIndex: number | null,
      transitionIdentity: string,
      context: string,
      parentProblem: string | null,
      supportedCompletion: boolean | null = null,
    ): void => {
      (decisions ?? []).forEach((decision, decisionIndex) => {
        const key = `decision:${transitionIndex ?? 'step'}:${decisionIndex}`;
        const identity = decision
          ? `${transitionIdentity}:decision:${decisionIdentity(decision)}`
          : key;
        const duplicates = decision
          ? decisions!.filter(
              (candidate) =>
                candidate &&
                decisionIdentity(candidate) === decisionIdentity(decision),
            ).length
          : 0;
        const problem =
          parentProblem ??
          (!decision?.decisionKind?.trim()
            ? 'Repair this incomplete decision before configuring recipients.'
            : duplicates !== 1
              ? 'This decision is not unique. Repair duplicate decisions before configuring recipients.'
              : null);
        events.push({
          key,
          identity,
          kind: 'decision',
          transitionIndex,
          decisionIndex,
          owner: decision,
          label: decision
            ? humanize(decision.decisionKind) +
              (decision.requiredOutcomeCode
                ? `: ${humanize(decision.requiredOutcomeCode)}`
                : '')
            : 'Incomplete decision',
          context,
          triggerLabel: 'when this required decision result is recorded',
          statusPairLabel: transitionIndex === null ? null : context,
          supportedCompletion,
          fingerprint: JSON.stringify(decision),
          ownerProblem: problem,
          problem,
        });
      });
    };
    addDecisions(step.requiredDecisions, null, 'step', 'Step decision', null);
    (step.allowedTransitions ?? []).forEach((transition, transitionIndex) => {
      const key = `transition:${transitionIndex}`;
      const identity = transition
        ? `transition:${transitionIdentity(transition)}`
        : key;
      const duplicate =
        transition &&
        step.allowedTransitions!.filter(
          (candidate) =>
            candidate &&
            transitionIdentity(candidate) === transitionIdentity(transition),
        ).length !== 1;
      const problem =
        !transition?.fromStateCode?.trim() || !transition.toStateCode?.trim()
          ? 'Repair this incomplete status change before configuring recipients.'
          : duplicate
            ? 'This status change is not unique. Repair duplicate status changes before configuring recipients.'
            : null;
      const label = transition
        ? `${humanize(transition.fromStateCode)} → ${humanize(transition.toStateCode)}`
        : 'Incomplete status change';
      const supportedCompletion =
        !!transition &&
        transition.fromStateCode?.trim().toLowerCase() === 'in_progress' &&
        transition.toStateCode?.trim().toLowerCase() === 'completed';
      events.push({
        key,
        identity,
        label,
        context: 'Status change',
        triggerLabel: 'after this status change completes',
        statusPairLabel: label,
        supportedCompletion,
        kind: 'transition',
        transitionIndex,
        decisionIndex: null,
        owner: transition,
        fingerprint: JSON.stringify(transition),
        ownerProblem: problem,
        problem,
      });
      if (transition)
        addDecisions(
          transition.requiredDecisions,
          transitionIndex,
          identity,
          label,
          problem,
          supportedCompletion,
        );
    });
    return events.map((event) => {
      const rules =
        event.owner?.notifications?.filter(
          (rule): rule is WorkflowNotificationRule => rule !== null,
        ) ?? [];
      const ambiguousRule = rules.some(
        (rule) =>
          !rule.id?.trim() ||
          rules.filter((candidate) => candidate.id === rule.id).length !== 1,
      );
      return ambiguousRule && !event.problem
        ? {
            ...event,
            problem:
              "Recipients include a missing or duplicate identifier. Repair this event's notification rules before editing recipients here.",
          }
        : event;
    });
  }

  private documentIdentity(document = this.document): string {
    return JSON.stringify([
      document?.workflowCode,
      document?.definitionVersionCode,
      document?.packReference,
      this.nodeId,
    ]);
  }
  private rejectChange(message: string): void {
    this.localError.set(message);
    this.rejectedChange = true;
    this.setPending(true);
  }
  private setPending(value: boolean): void {
    if (this.pending() === value) return;
    this.pending.set(value);
    this.pendingChangesChange.emit(value);
  }
}

function transitionIdentity(transition: WorkflowAllowedTransition): string {
  return JSON.stringify([
    transition.fromStateCode?.trim().toLowerCase(),
    transition.toStateCode?.trim().toLowerCase(),
  ]);
}
function decisionIdentity(decision: WorkflowDecisionRequirement): string {
  return JSON.stringify([
    decision.decisionKind?.trim().toLowerCase(),
    decision.requiredOutcomeCode?.trim().toLowerCase() ?? 'approved',
    decision.reaction?.reactionCode?.trim().toLowerCase() ?? 'permit',
    decision.reaction?.targetStateCode?.trim().toLowerCase() ?? '',
  ]);
}
function humanize(value: string | null | undefined): string {
  const label = value?.replace(/[._-]+/g, ' ').trim();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : 'Unspecified';
}
